import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Bell, BellRing, X, Check, CheckCheck, Trash2, 
  ClipboardList, AlertCircle, Clock, Calendar, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { toast as sonnerToast } from 'sonner';
import {
  getNotifications, getUnreadCount, markNotificationRead,
  markAllNotificationsRead, deleteNotification, checkTaskReminders
} from '../services/api';

// Play notification sound
const playNotificationSound = () => {
  try {
    // Create a simple beep sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    
    // Fade out
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    console.log('Could not play sound:', e);
  }
};

// Show in-app toast notification with optional click action
const showInAppNotification = (title, message, type = 'default', onClickAction = null) => {
  // Play sound
  playNotificationSound();
  
  // Toast options
  const toastOptions = {
    description: message,
    duration: 8000,
  };
  
  // Add click action if provided
  if (onClickAction) {
    toastOptions.action = {
      label: 'Ver tarefa',
      onClick: onClickAction,
    };
  }
  
  // Show toast using sonner
  if (type === 'urgent') {
    sonnerToast.error(title, toastOptions);
  } else {
    sonnerToast.success(title, toastOptions);
  }
};

// Request notification permission (still try browser notifications as backup)
const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return false;
};

// Show browser notification
const showBrowserNotification = (title, body) => {
  console.log('Attempting to show notification:', title, body);
  console.log('Notification permission:', Notification.permission);
  
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return;
  }
  
  if (Notification.permission === 'granted') {
    try {
      // Simple notification without icon first
      const notification = new Notification(title, {
        body: body,
        requireInteraction: true,
        tag: `gelatti-${Date.now()}`
      });
      
      console.log('Notification created:', notification);
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
      
      notification.onerror = (e) => {
        console.error('Notification error:', e);
      };
      
      // Auto close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (e) {
      console.error('Error showing notification:', e);
      alert('Erro ao mostrar notificação: ' + e.message);
    }
  } else {
    console.log('Notification permission not granted:', Notification.permission);
  }
};

// Notification type icons and colors
const getNotificationStyle = (type) => {
  switch (type) {
    case 'new_task':
      return { icon: ClipboardList, color: 'text-blue-500', bg: 'bg-blue-50' };
    case 'task_reminder':
      return { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50' };
    case 'task_due_today':
      return { icon: Calendar, color: 'text-orange-500', bg: 'bg-orange-50' };
    case 'task_overdue':
      return { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-50' };
    default:
      return { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-50' };
  }
};

// Format time ago
const formatTimeAgo = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

// Single Notification Item
const NotificationItem = ({ notification, onRead, onDelete, onNavigate }) => {
  const style = getNotificationStyle(notification.type);
  const Icon = style.icon;
  
  const handleClick = () => {
    if (notification.related_task_id && onNavigate) {
      onNavigate('tasks', notification.related_task_id);
      if (!notification.read) {
        onRead(notification.id);
      }
    }
  };
  
  const hasTask = notification.related_task_id && onNavigate;
  
  return (
    <div 
      className={`p-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${!notification.read ? 'bg-blue-50/50' : ''} ${hasTask ? 'cursor-pointer' : ''}`}
      onClick={hasTask ? handleClick : undefined}
    >
      <div className="flex gap-3">
        <div className={`p-2 rounded-lg ${style.bg} flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${style.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className={`text-sm font-medium ${!notification.read ? 'text-slate-800' : 'text-slate-600'}`}>
                {notification.title}
              </p>
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                {notification.message}
              </p>
            </div>
            <span className="text-xs text-slate-400 flex-shrink-0">
              {formatTimeAgo(notification.created_at)}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-2">
            {hasTask && (
              <span className="text-xs text-teal-600 flex items-center gap-1">
                <ClipboardList className="w-3 h-3" />
                Clique para ver tarefa
              </span>
            )}
            {!notification.read && (
              <button
                onClick={(e) => { e.stopPropagation(); onRead(notification.id); }}
                className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                Marcar como lida
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
              className="text-xs text-slate-400 hover:text-red-500 flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Notifications Component
const NotificationsPanel = ({ userId, onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const previousIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const [notifs, countData] = await Promise.all([
        getNotifications(userId),
        getUnreadCount(userId)
      ]);
      
      // Check for NEW notifications (not seen before) and show in-app toast
      if (!isFirstLoadRef.current && notifs.length > 0) {
        const newNotifs = notifs.filter(n => !previousIdsRef.current.has(n.id) && !n.read);
        
        if (newNotifs.length > 0) {
          newNotifs.forEach(n => {
            // Show in-app toast with click action to open task
            const isUrgent = n.type === 'task_overdue' || n.type === 'task_due_today';
            
            // Create click handler for this notification
            const handleToastClick = n.related_task_id && onNavigate 
              ? () => onNavigate('tasks', n.related_task_id)
              : null;
            
            showInAppNotification(n.title, n.message, isUrgent ? 'urgent' : 'default', handleToastClick);
            
            // Also try browser notification if permission granted
            if (hasPermission) {
              showBrowserNotification(n.title, n.message);
            }
          });
        }
        
        // Update refs with current IDs
        previousIdsRef.current = new Set(notifs.map(n => n.id));
      } else if (isFirstLoadRef.current) {
        // First load - just save the IDs
        console.log('Primeira carga - salvando', notifs.length, 'IDs');
        previousIdsRef.current = new Set(notifs.map(n => n.id));
        isFirstLoadRef.current = false;
      }
      
      setNotifications(notifs);
      setUnreadCount(countData.count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, hasPermission]);

  // Check task reminders and create notifications
  const checkReminders = useCallback(async () => {
    try {
      await checkTaskReminders();
      await fetchNotifications();
    } catch (error) {
      console.error('Error checking reminders:', error);
    }
  }, [fetchNotifications]);

  // Initial load and setup
  useEffect(() => {
    if (userId) {
      // Check if permission was already granted
      if ('Notification' in window) {
        setHasPermission(Notification.permission === 'granted');
      }
      
      // Initial fetch
      fetchNotifications();
      checkReminders();
      
      // Set up polling interval (every 10 seconds for real-time feel)
      const interval = setInterval(() => {
        fetchNotifications();
      }, 10000);
      
      // Check reminders every 5 minutes
      const reminderInterval = setInterval(() => {
        checkReminders();
      }, 300000);
      
      return () => {
        clearInterval(interval);
        clearInterval(reminderInterval);
      };
    }
  }, [userId, fetchNotifications, checkReminders]);

  // Refresh when panel opens
  useEffect(() => {
    if (isOpen && userId) {
      fetchNotifications();
    }
  }, [isOpen, userId]);

  // Handle mark as read
  const handleMarkRead = async (notificationId) => {
    try {
      await markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle mark all as read
  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead(userId);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Handle delete
  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      const deleted = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Enable notifications
  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission();
    setHasPermission(granted);
    if (granted) {
      showBrowserNotification('Notificações ativadas!', 'Você receberá alertas de tarefas.');
    }
  };

  // Test notification manually
  const handleTestNotification = () => {
    // Show in-app toast
    showInAppNotification(
      'Teste de Notificação', 
      'Se você está vendo isso, as notificações estão funcionando!'
    );
    
    // Also try browser notification
    if (hasPermission) {
      try {
        new Notification('Teste Gelatti', {
          body: 'Notificação do Windows',
          requireInteraction: false
        });
      } catch (e) {
        console.log('Browser notification failed:', e);
      }
    }
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 animate-pulse" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-bounce">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
            {/* Header */}
            <div className="p-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-slate-600" />
                <span className="font-medium text-slate-800">Notificações</span>
                {unreadCount > 0 && (
                  <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full">
                    {unreadCount} {unreadCount === 1 ? 'nova' : 'novas'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={fetchNotifications}
                  className={`p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors ${loading ? 'animate-spin' : ''}`}
                  title="Atualizar"
                  disabled={loading}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                    title="Marcar todas como lidas"
                  >
                    <CheckCheck className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {loading && notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-2" />
                  Carregando...
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhuma notificação</p>
                </div>
              ) : (
                notifications.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={handleMarkRead}
                    onDelete={handleDelete}
                    onNavigate={(tab, taskId) => {
                      setIsOpen(false);
                      onNavigate(tab, taskId);
                    }}
                  />
                ))
              )}
            </div>

            {/* Footer - Enable notifications */}
            {!hasPermission && (
              <div className="p-3 border-t border-slate-100 bg-amber-50">
                <button
                  onClick={handleEnableNotifications}
                  className="w-full py-2 px-4 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <BellRing className="w-4 h-4" />
                  Ativar notificações do Windows (opcional)
                </button>
              </div>
            )}
            
            {/* Show confirmation when enabled */}
            {hasPermission && (
              <div className="p-2 border-t border-slate-100 bg-green-50">
                <p className="text-xs text-green-600 flex items-center justify-center gap-1">
                  <Check className="w-3 h-3" />
                  Notificações do Windows ativadas
                </p>
              </div>
            )}
            
            {/* Always show test button */}
            <div className="p-2 border-t border-slate-100">
              <button
                onClick={handleTestNotification}
                className="w-full py-2 px-4 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
              >
                <Bell className="w-4 h-4" />
                Testar notificação
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsPanel;
